import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  askEndpoint,
  ASSISTANT_RAG_CONFIG,
  resolveRagAskConfig,
  type RagAskConfig,
  type ResolvedRagAskConfig,
} from '../config/rag-ask.config';
import {
  createMessage,
  toAskHistory,
  type AskApiResponse,
  type AskRequestBody,
  type AskSource,
  type AskStreamChunk,
  type ChatMessage,
} from '../models/ask.models';
import { requestAskWithHttpClient } from '../util/http-transport';

/** Shape persisted to web storage — `Date` is stored as an ISO string. */
interface PersistedMessage extends Omit<ChatMessage, 'timestamp' | 'status'> {
  timestamp: string;
  status: ChatMessage['status'];
}

/**
 * Conversation state and transport for the ask widget.
 *
 * Reuses the host's HttpClient so its authentication, token refresh and CSRF
 * interceptors apply to ask requests. An explicit fetchFn or fetch transport
 * remains available for custom-element hosts. Conversation state uses signals.
 *
 * Provided at component level, so every widget instance owns its own thread.
 */
@Injectable()
export class RagAskService {
  private readonly http = inject(HttpClient, { optional: true });
  private readonly injectedConfig = inject<RagAskConfig | null>(ASSISTANT_RAG_CONFIG, { optional: true });
  private readonly destroyRef = inject(DestroyRef);

  private readonly _overrides = signal<Partial<RagAskConfig>>({});
  private readonly _messages = signal<readonly ChatMessage[]>([]);
  private readonly _isStreaming = signal(false);
  private readonly _lastError = signal<string | null>(null);

  /** Effective configuration: defaults ← DI config ← instance overrides. */
  readonly config = computed<ResolvedRagAskConfig>(() =>
    resolveRagAskConfig(this.injectedConfig, this._overrides()),
  );

  readonly messages = this._messages.asReadonly();
  readonly isStreaming = this._isStreaming.asReadonly();
  readonly lastError = this._lastError.asReadonly();
  readonly hasConversation = computed(() => this._messages().length > 0);

  private abortController: AbortController | null = null;
  private rafId: number | null = null;
  private revealToken = 0;
  private restored = false;

  constructor() {
    this.destroyRef.onDestroy(() => this.cancelInFlight());
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Configuration
  // ───────────────────────────────────────────────────────────────────────────

  /** Apply per-instance configuration (called by the widget from its inputs). */
  setConfig(overrides: Partial<RagAskConfig>): void {
    this._overrides.set(overrides);
    if (!this.restored) {
      this.restored = true;
      this.restore();
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Actions
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Ask a question. Adds the user turn plus a streaming assistant turn, then
   * fills the latter from the API. No-ops while a request is in flight.
   */
  async send(text: string, collection?: string | number | null): Promise<void> {
    const cfg = this.config();
    const trimmed = text.trim();
    if (!trimmed || trimmed.length < cfg.minChars || this._isStreaming()) return;

    // Snapshot before appending the current turn, otherwise the question
    // would include itself in its own history.
    const history = cfg.history ? toAskHistory(this._messages(), cfg.maxHistoryTurns) : [];
    const scope = collection !== undefined ? collection : cfg.collection;

    const assistant = createMessage('assistant', '', 'streaming');
    this._messages.update((msgs) => [
      ...msgs,
      createMessage('user', trimmed.slice(0, cfg.maxChars)),
      assistant,
    ]);
    this._isStreaming.set(true);
    this._lastError.set(null);

    let body: AskRequestBody = {
      query: trimmed,
      ...(scope !== null && scope !== undefined ? { collection: scope } : {}),
      ...(history.length ? { history } : {}),
      ...cfg.extraBody,
    };
    if (cfg.transformRequest) body = cfg.transformRequest(body);

    this.abortController = new AbortController();

    try {
      const response = await this.request(cfg, body, this.abortController.signal);

      if (!response.ok) {
        throw new HttpStatusError(response.status, await safeText(response));
      }

      if (this.isStreamingResponse(cfg, response)) {
        await this.consumeStream(assistant.id, response, cfg);
        this.finalize(assistant.id);
      } else {
        const payload = (await response.json()) as AskApiResponse;
        const answer = payload.answer ?? '';
        const irrelevant = answer.trim() === cfg.noAnswerText.trim();
        // The backend still returns the retrieved chunks when its system prompt
        // produced the "nothing relevant" fallback; hiding them avoids implying
        // they backed the answer.
        this.setSources(assistant.id, irrelevant ? [] : payload.sources ?? []);
        await this.reveal(assistant.id, answer, cfg);
        this.finalize(assistant.id);
      }
      this.persist();
    } catch (error) {
      if (isAbort(error)) {
        // Deliberate stop: keep whatever text arrived.
        this.finalize(assistant.id);
        this.persist();
      } else {
        this.fail(assistant.id, this.errorMessage(error, cfg));
      }
    } finally {
      this.abortController = null;
      this._isStreaming.set(false);
    }
  }

  /** Stop the in-flight request and the reveal animation, keeping partial text. */
  stop(): void {
    this.cancelInFlight();
  }

  /** Clear the conversation. */
  reset(): void {
    this.cancelInFlight();
    this._messages.set([]);
    this._lastError.set(null);
    this._isStreaming.set(false);
    this.persist();
  }

  /** Re-ask the last user question, dropping the answer it produced. */
  retryLast(): void {
    // Without this guard the thread would be truncated and `send` would then
    // bail out, losing the turn.
    if (this._isStreaming()) return;
    const msgs = this._messages();
    const lastUser = [...msgs].reverse().find((m) => m.role === 'user');
    if (!lastUser) return;
    const index = msgs.indexOf(lastUser);
    this._messages.set(msgs.slice(0, index));
    void this.send(lastUser.content);
  }

  /**
   * Fetch a link as a blob and save it. Used for source downloads so that the
   * request carries the host's credentials and is not blocked as mixed content.
   */
  async downloadFile(url: string, filename: string): Promise<void> {
    const cfg = this.config();
    try {
      const response = await (cfg.fetchFn ?? fetch)(url, {
        ...cfg.requestInit,
        method: 'GET',
        credentials: cfg.withCredentials ? 'include' : 'same-origin',
        headers: await this.resolveHeaders(cfg, false),
      });
      if (!response.ok) throw new HttpStatusError(response.status, '');
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filename || 'document';
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Give the browser a tick to start the download before revoking.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
    } catch {
      // Fall back to letting the browser handle the link itself.
      window.open(url, '_blank', 'noopener');
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Transport
  // ───────────────────────────────────────────────────────────────────────────

  private async request(
    cfg: ResolvedRagAskConfig,
    body: AskRequestBody,
    signal: AbortSignal,
  ): Promise<Response> {
    const headers = await this.resolveHeaders(cfg, true);
    if (signal.aborted) throw new DOMException('Request aborted', 'AbortError');

    if (!cfg.fetchFn && cfg.transport !== 'fetch') {
      if (this.http) {
        return requestAskWithHttpClient(
          this.http, askEndpoint(cfg), body, headers,
          cfg.withCredentials, cfg.streamMode, signal,
        );
      }
      if (cfg.transport === 'http') {
        throw new Error('The host must provide HttpClient to use transport="http".');
      }
    }

    const doFetch = cfg.fetchFn ?? ((input: string, init: RequestInit) => fetch(input, init));
    return doFetch(askEndpoint(cfg), {
      ...cfg.requestInit,
      method: 'POST',
      credentials: cfg.withCredentials ? 'include' : 'same-origin',
      headers,
      body: JSON.stringify(body),
      signal,
    });
  }

  private async resolveHeaders(
    cfg: ResolvedRagAskConfig,
    json: boolean,
  ): Promise<Record<string, string>> {
    const extra =
      typeof cfg.headers === 'function' ? await cfg.headers() : cfg.headers ?? {};
    // Preserve headers supplied through requestInit as well as cfg.headers.
    // Headers normalizes casing, so an explicit Authorization replaces a
    // previously configured authorization header instead of duplicating it.
    const headers = new Headers(json
      ? { 'Content-Type': 'application/json', Accept: 'application/json, text/event-stream' }
      : undefined);
    new Headers(cfg.requestInit.headers).forEach((value, key) => headers.set(key, value));
    new Headers(extra).forEach((value, key) => headers.set(key, value));
    const resolved: Record<string, string> = {};
    headers.forEach((value, key) => { resolved[key] = value; });
    return resolved;
  }

  private isStreamingResponse(cfg: ResolvedRagAskConfig, response: Response): boolean {
    if (cfg.streamMode === 'json') return false;
    if (cfg.streamMode === 'stream') return !!response.body;
    const type = response.headers.get('content-type') ?? '';
    return (
      !!response.body && (type.includes('text/event-stream') || type.includes('x-ndjson'))
    );
  }

  /** Read an SSE / NDJSON body, appending deltas as they arrive. */
  private async consumeStream(
    msgId: string,
    response: Response,
    cfg: ResolvedRagAskConfig,
  ): Promise<void> {
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let accumulated = '';
    let pendingContent: string | null = null;
    let flushScheduled = false;

    // Coalesce rapid token updates onto a single animation frame so a burst
    // of SSE chunks does not force one Angular signal write + Markdown pass
    // per token. Sources and errors still apply immediately.
    const flushContent = (): void => {
      flushScheduled = false;
      if (pendingContent === null) return;
      this.patch(msgId, { content: pendingContent });
      pendingContent = null;
    };

    const scheduleFlush = (): void => {
      if (flushScheduled) return;
      flushScheduled = true;
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(flushContent);
      } else {
        flushContent();
      }
    };

    const applyChunk = (chunk: AskStreamChunk): void => {
      if (chunk.error) throw new Error(chunk.error);
      if (chunk.sources?.length) this.setSources(msgId, chunk.sources);
      if (typeof chunk.answer === 'string') accumulated = chunk.answer;
      else if (chunk.delta) accumulated += chunk.delta;
      else return;
      pendingContent = accumulated;
      scheduleFlush();
    };

    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Frames are separated by a blank line (SSE) or a newline (NDJSON).
        const frames = buffer.split(/\r?\n\r?\n|\r?\n/);
        buffer = frames.pop() ?? '';
        for (const frame of frames) {
          const chunk = parseFrame(frame);
          if (chunk) applyChunk(chunk);
        }
      }

      const tail = parseFrame(buffer);
      if (tail) applyChunk(tail);
    } finally {
      // Ensure the last delta is visible even if the loop exited mid-frame.
      if (pendingContent !== null) flushContent();
    }

    if (!accumulated.trim()) {
      this.patch(msgId, { content: cfg.labels.genericError, status: 'error' });
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Reveal animation
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Reveal a complete answer progressively. Unlike a fixed per-character delay,
   * the whole answer is revealed within a target duration, so a long answer
   * does not take half a minute to appear.
   *
   * Uses code-unit slicing (`String.prototype.slice`) rather than `Array.from`
   * + `join`: the latter allocates a full character array and rebuilds a
   * string on every animation frame. Surrogate pairs may split mid-reveal for
   * a frame or two, which is acceptable for a typewriter effect.
   */
  private reveal(msgId: string, full: string, cfg: ResolvedRagAskConfig): Promise<void> {
    const duration = cfg.typewriter === true ? 900 : cfg.typewriter === false ? 0 : cfg.typewriter;
    const length = full.length;

    if (!duration || length === 0 || typeof requestAnimationFrame !== 'function' || prefersReducedMotion()) {
      this.patch(msgId, { content: full });
      return Promise.resolve();
    }

    const token = ++this.revealToken;
    const start = now();
    let lastCount = 0;

    return new Promise<void>((resolve) => {
      const step = (): void => {
        if (token !== this.revealToken) {
          resolve();
          return;
        }
        const progress = Math.min(1, (now() - start) / duration);
        const count = Math.max(1, Math.floor(progress * length));
        // Skip redundant patches when the frame advanced but the char count did not.
        if (count !== lastCount) {
          lastCount = count;
          this.patch(msgId, { content: full.slice(0, count) });
        }

        if (progress >= 1) {
          this.rafId = null;
          resolve();
        } else {
          this.rafId = requestAnimationFrame(step);
        }
      };
      this.rafId = requestAnimationFrame(step);
    });
  }

  // ───────────────────────────────────────────────────────────────────────────
  // State helpers
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Update a message by id. When the target is the last entry (the common
   * streaming case), rebuild only that slot instead of mapping the whole
   * history — O(1) allocation of a shallow-copied tail rather than O(n).
   */
  private patch(msgId: string, patch: Partial<ChatMessage>): void {
    this._messages.update((msgs) => {
      const last = msgs.length - 1;
      if (last >= 0 && msgs[last]!.id === msgId) {
        const next = msgs.slice();
        next[last] = { ...msgs[last]!, ...patch };
        return next;
      }
      return msgs.map((m) => (m.id === msgId ? { ...m, ...patch } : m));
    });
  }

  private setSources(msgId: string, sources: AskSource[]): void {
    if (!sources.length) return;
    this.patch(msgId, { sources });
  }

  private finalize(msgId: string): void {
    this._messages.update((msgs) => {
      const last = msgs.length - 1;
      if (last >= 0 && msgs[last]!.id === msgId) {
        const cur = msgs[last]!;
        if (cur.status !== 'streaming') return msgs;
        const next = msgs.slice();
        next[last] = { ...cur, status: 'done' };
        return next;
      }
      let changed = false;
      const next = msgs.map((m) => {
        if (m.id !== msgId || m.status !== 'streaming') return m;
        changed = true;
        return { ...m, status: 'done' as const };
      });
      return changed ? next : msgs;
    });
  }

  private fail(msgId: string, message: string): void {
    this._lastError.set(message);
    this.patch(msgId, { status: 'error', content: message });
    this.persist();
  }

  private errorMessage(error: unknown, cfg: ResolvedRagAskConfig): string {
    if (error instanceof HttpStatusError || error instanceof HttpErrorResponse) {
      if (error.status === 429) {
        return 'Trop de questions envoyées. Patientez quelques instants.';
      }
      if (error.status === 0 || error.status >= 500) return cfg.labels.networkError;
      const detail = responseDetail(error instanceof HttpStatusError ? error.detail : error.error);
      if (detail) return detail;
      if (error.status === 401) return 'Authentification requise. Reconnectez-vous puis réessayez.';
      if (error.status === 403) return 'Vous ne disposez pas des droits nécessaires pour cette demande.';
      return cfg.labels.genericError;
    }
    if (error instanceof TypeError) return cfg.labels.networkError;
    return cfg.labels.genericError;
  }

  private cancelInFlight(): void {
    this.revealToken++;
    if (this.rafId !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.abortController?.abort();
    this.abortController = null;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Optional persistence
  // ───────────────────────────────────────────────────────────────────────────

  private storage(): Storage | null {
    const cfg = this.config();
    if (cfg.persist === 'none' || typeof window === 'undefined') return null;
    try {
      return cfg.persist === 'local' ? window.localStorage : window.sessionStorage;
    } catch {
      return null; // Storage blocked (private mode, cookie policy).
    }
  }

  private persist(): void {
    const store = this.storage();
    if (!store) return;
    const payload: PersistedMessage[] = this._messages()
      .filter((m) => m.status === 'done')
      .map((m) => ({ ...m, timestamp: m.timestamp.toISOString() }));
    try {
      store.setItem(this.config().storageKey, JSON.stringify(payload));
    } catch {
      /* quota or blocked — persistence is best-effort */
    }
  }

  private restore(): void {
    const store = this.storage();
    if (!store) return;
    try {
      const raw = store.getItem(this.config().storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as PersistedMessage[];
      if (!Array.isArray(parsed)) return;
      this._messages.set(
        parsed
          .filter((m) => m && typeof m.content === 'string')
          .map((m) => ({ ...m, timestamp: new Date(m.timestamp), status: 'done' as const })),
      );
    } catch {
      /* corrupt payload — start fresh */
    }
  }
}

class HttpStatusError extends Error {
  constructor(readonly status: number, readonly detail: string) {
    super(`HTTP ${status}`);
  }
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException ? error.name === 'AbortError' : false;
}

async function safeText(response: Response): Promise<string> {
  try {
    return (await response.text()).slice(0, 500);
  } catch {
    return '';
  }
}

/** DRF supplies actionable authentication/permission errors as { detail }. */
function responseDetail(body: unknown): string | null {
  try {
    const payload: unknown = typeof body === 'string' ? JSON.parse(body) : body;
    if (payload && typeof payload === 'object' && 'detail' in payload) {
      const detail = (payload as { detail?: unknown }).detail;
      return typeof detail === 'string' ? detail.slice(0, 500) : null;
    }
  } catch { /* Non-JSON error bodies use the translated fallback. */ }
  return null;
}

/** Parse one SSE frame (`data: {...}`) or one NDJSON line. */
function parseFrame(frame: string): AskStreamChunk | null {
  const trimmed = frame.trim();
  if (!trimmed) return null;

  const payload = trimmed.startsWith('data:')
    ? trimmed.replace(/^data:\s*/, '')
    : trimmed.startsWith(':') || /^(event|id|retry):/.test(trimmed)
      ? ''
      : trimmed;

  if (!payload || payload === '[DONE]') return null;
  if (!payload.startsWith('{') && !payload.startsWith('[')) {
    return { delta: payload };
  }
  try {
    return JSON.parse(payload) as AskStreamChunk;
  } catch {
    return null;
  }
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}
